import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sparkles, Upload, Image as ImageIcon, X, Check } from "lucide-react";
import { CATEGORY_GALLERY } from "../constants/media";
import { categoryCover } from "../lib/covers";

const STEPS = ["Detalhes", "Localização", "Descrição", "Foto"];

const MAX_COVER_BYTES = 5 * 1024 * 1024;

/** Campo de seleção do wizard (definido fora do componente para não recriar o tipo em cada render). */
const SelectField = ({ label, k, options, testId, value, onChange }) => (
  <div>
    <Label className="mb-1.5 block">{label}</Label>
    <Select value={value[k]} onValueChange={(v) => onChange(k, v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Seleciona..." /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const slugify = (text) => String(text || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const PublishWizard = ({ open, onOpenChange, meta, onPublished }) => {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ title: "", category: "", district: "", concelho: "", regime: "", work_mode: "", salary_min: "", salary_max: "", description: "", requirements: "" });
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  // Foto: upload OU preset OU nada (default = usar capa da empresa/setor)
  const [coverMode, setCoverMode] = useState("default"); // 'default' | 'upload' | 'preset'
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverPreset, setCoverPreset] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setF({ ...f, [k]: v });

  const resetAll = () => {
    setStep(0);
    setF({ title: "", category: "", district: "", concelho: "", regime: "", work_mode: "", salary_min: "", salary_max: "", description: "", requirements: "" });
    setCoverMode("default");
    setCoverFile(null);
    setCoverPreview(null);
    setCoverPreset(null);
    setAiKeywords("");
  };

  const generateAI = async () => {
    if (aiKeywords.trim().length < 3) { toast.error("Escreve pelo menos 3-4 palavras-chave"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/company/jobs/generate", { keywords: aiKeywords, title: f.title || null, category: f.category || null });
      setF({ ...f, title: f.title || data.title || "", description: data.description || "", requirements: data.requirements || "" });
      toast.success("Anúncio gerado — revê e ajusta antes de submeter");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAiLoading(false); }
  };

  const canNext = step === 0 ? f.title && f.category && f.regime
    : step === 1 ? f.district && f.concelho && f.work_mode
    : step === 2 ? f.description.length >= 20
    : true;

  const pickFile = (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("A imagem tem de ser PNG, JPG ou WEBP");
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      toast.error("A imagem não pode exceder 5 MB");
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setCoverMode("upload");
    setCoverPreset(null);
  };

  const pickPreset = (key) => {
    setCoverPreset(key);
    setCoverMode("preset");
    setCoverFile(null);
    setCoverPreview(null);
  };

  const useDefault = () => {
    setCoverMode("default");
    setCoverFile(null);
    setCoverPreview(null);
    setCoverPreset(null);
  };

  const publish = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // 1. Criar a vaga
      const { data: job } = await api.post("/company/jobs", {
        ...f,
        salary_min: f.salary_min ? parseInt(f.salary_min) : null,
        salary_max: f.salary_max ? parseInt(f.salary_max) : null,
      });
      // 2. Aplicar cover (se não default) — nunca falha a criação
      if (job?.id) {
        try {
          if (coverMode === "upload" && coverFile) {
            const body = new FormData();
            body.append("file", coverFile);
            await api.post(`/company/jobs/${job.id}/cover`, body);
          } else if (coverMode === "preset" && coverPreset) {
            await api.post(`/company/jobs/${job.id}/cover-preset`, { preset: coverPreset });
          }
        } catch (e) {
          toast.warning("Vaga criada, mas a foto de capa não pôde ser aplicada — podes fazê-lo mais tarde.");
        }
      }
      toast.success("Vaga submetida! Fica ativa após moderação da equipa.");
      onOpenChange(false);
      resetAll();
      onPublished();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  // Presets sugeridos: mesma categoria da vaga
  const suggestedBase = slugify(f.category) || "outros";
  const suggested = CATEGORY_GALLERY[suggestedBase] || CATEGORY_GALLERY.outros;
  const previewFallback = categoryCover(f.category);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAll(); onOpenChange(v); }}>
      <DialogContent data-testid="publish-wizard" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading tracking-tighter">Publicar vaga</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1 sm:gap-2 mb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 sm:gap-2 flex-1">
              <div className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold ${i <= step ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</div>
              <span className={`text-[11px] sm:text-xs ${i <= step ? "text-slate-900 font-semibold" : "text-slate-400"}`}>{s}</span>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Título da vaga</Label>
              <Input data-testid="wizard-title-input" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex.: Empregado/a de Mesa" />
            </div>
            <SelectField value={f} onChange={set} label="Categoria" k="category" options={meta?.categories || []} testId="wizard-category-select" />
            <SelectField value={f} onChange={set} label="Regime" k="regime" options={meta?.regimes || []} testId="wizard-regime-select" />
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <SelectField value={f} onChange={set} label="Distrito" k="district" options={Object.keys(meta?.districts || {})} testId="wizard-district-select" />
            {f.district && <SelectField value={f} onChange={set} label="Concelho" k="concelho" options={meta?.districts?.[f.district] || []} testId="wizard-concelho-select" />}
            <SelectField value={f} onChange={set} label="Modalidade" k="work_mode" options={meta?.work_modes || []} testId="wizard-workmode-select" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Salário mín. (€)</Label>
                <Input data-testid="wizard-salary-min" type="number" value={f.salary_min} onChange={(e) => set("salary_min", e.target.value)} placeholder="900" />
              </div>
              <div>
                <Label className="mb-1.5 block">Salário máx. (€)</Label>
                <Input data-testid="wizard-salary-max" type="number" value={f.salary_max} onChange={(e) => set("salary_max", e.target.value)} placeholder="1200" />
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700 flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5" /> Gerar com IA
              </p>
              <div className="flex gap-2">
                <Input data-testid="wizard-ai-keywords" value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)}
                  placeholder="Ex.: cozinheiro, grelhados, jantares, equipa jovem" className="bg-white" />
                <Button data-testid="wizard-ai-generate-btn" type="button" size="sm" disabled={aiLoading} onClick={generateAI}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white shrink-0 h-10">
                  {aiLoading ? "A gerar..." : "Gerar"}
                </Button>
              </div>
              <p className="text-xs text-amber-700/70 mt-2">Escreve palavras-chave e a IA escreve a descrição e os requisitos por ti.</p>
            </div>
            <div>
              <Label className="mb-1.5 block">Descrição (mín. 20 caracteres)</Label>
              <Textarea data-testid="wizard-description-input" rows={5} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Descreve a função, condições e o que ofereces..." />
            </div>
            <div>
              <Label className="mb-1.5 block">Requisitos (opcional)</Label>
              <Textarea data-testid="wizard-requirements-input" rows={3} value={f.requirements} onChange={(e) => set("requirements", e.target.value)} />
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Escolhe a foto que aparecerá no cartão da vaga. Podes carregar uma foto própria, escolher da galeria do teu setor, ou deixar por defeito (usa a capa da empresa).
            </p>
            {/* Preview atual */}
            <div className="rounded-xl overflow-hidden border border-black/5 bg-slate-100 h-40">
              <img
                src={coverPreview || (coverPreset ? (CATEGORY_GALLERY[suggestedBase]?.[parseInt(coverPreset.split("-")[1] || "1", 10) - 1] || previewFallback) : previewFallback)}
                alt="Pré-visualização"
                data-testid="wizard-cover-preview"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">
              A usar:{" "}
              <span className="font-semibold text-slate-700" data-testid="wizard-cover-source">
                {coverMode === "upload" ? "foto carregada" : coverMode === "preset" ? "foto da galeria" : "foto do setor (por defeito)"}
              </span>
            </p>

            {/* Ações */}
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                data-testid="wizard-cover-file-input" className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])} />
              <Button type="button" data-testid="wizard-cover-upload-btn"
                variant="outline" className="rounded-full"
                onClick={() => fileRef.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Carregar foto
              </Button>
              {coverMode !== "default" && (
                <Button type="button" data-testid="wizard-cover-reset-btn"
                  variant="ghost" className="rounded-full text-slate-500" onClick={useDefault}>
                  <X className="w-3.5 h-3.5 mr-1" /> Usar padrão
                </Button>
              )}
            </div>

            {/* Sugestões da galeria do setor da vaga */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Galeria de {f.category || "Outros"}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {suggested.map((url, idx) => {
                  const key = `${suggestedBase}-${idx + 1}`;
                  const active = coverMode === "preset" && coverPreset === key;
                  return (
                    <button key={key} type="button"
                      data-testid={`wizard-cover-preset-${key}`}
                      onClick={() => pickPreset(key)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-colors ${
                        active ? "border-amber-500" : "border-transparent hover:border-amber-300"
                      }`}>
                      <img src={url} alt={`Opção ${idx + 1}`} className="w-full h-20 object-cover" />
                      {active && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="ghost" className="rounded-full" disabled={step === 0 || submitting} onClick={() => setStep(step - 1)}>← Anterior</Button>
          {step < 3 ? (
            <Button data-testid="wizard-next-btn" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={!canNext} onClick={() => setStep(step + 1)}>Seguinte →</Button>
          ) : (
            <Button data-testid="wizard-publish-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" disabled={submitting} onClick={publish}>
              {submitting ? "A submeter..." : "Submeter vaga"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
